import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PhoneNumberUtil } from 'google-libphonenumber';

/**
 * Validates a phone number using Google's libphonenumber library.
 * Optionally formats the phone number to international format if valid.
 *
 * @param countryCode - The default country code to use for parsing (default: 'AT' for Austria)
 * @param autoFormat - Whether to automatically format the phone number to E164 format (default: true)
 * @returns A validator function that returns an error map with `invalidPhoneNumber` if validation fails, otherwise null.
 */
export function phoneNumberValidator(countryCode = 'AT', autoFormat = true): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Don't validate empty values, let 'required' validator handle it
    }

    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const parsedNumber = phoneUtil.parse(control.value, countryCode);
      const isValid = phoneUtil.isValidNumber(parsedNumber);

      // Format the number to international format and update the control value if autoFormat is enabled
      if (autoFormat) {
        const formattedNumber = phoneUtil.format(parsedNumber, 0); // 0 = E164 format
        if (formattedNumber !== control.value) {
          // Update the control value without triggering valueChanges event
          control.setValue(formattedNumber, { emitEvent: false });
        }
      }

      return isValid ? null : { invalidPhoneNumber: { value: control.value } };
    } catch {
      return { invalidPhoneNumber: { value: control.value } };
    }
  };
}
